/* This mixin provides methods for getting form schemas */
export default {
  methods: {
    getAssessmentSchema() {
      const schema = {
        groups: [
          this.getBackground(),
          this.getGoals(),
          this.getPropertyOverview(),
          this.getResourceAnalysis(),
        ],
      };
      return schema;
    },
    getBackground() {
      return {
        legend: 'Background',
        headerName: 'Background',
        fields: [
          {
            type: 'select',
            label: '<span class="label-name">Preparer</span>',
            values: this.tualatinContacts
              .map((contact) => ({
                id: contact.contacT_ID,
                name: `${contact.firsT_NAME} ${contact.lasT_NAME}`,
              })),
            model: 'background.preparerId',
            required: true,
            styleClasses: 'row',
          },
          {
            type: 'customDatepicker',
            label: '<span class="label-name">Prepared Date</span>',
            placeholder: 'Select Prepared Date',
            model: 'background.preparedDate',
            required: true,
            styleClasses: 'row',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Landowner Information & Ownership</span><br/><span class="help-text">Provide a brief narrative of the ownership situation. Examples include single or family ownership, length of ownership, etc. (Max 750 characters)</span>',
            model: 'background.landownerInformation',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Historical Use</span><br/><span class="help-text">Provide a brief narrative of the historical use of the property. (Max 750 characters)</span>',
            model: 'background.historicalUse',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Current Use</span><br/><span class="help-text">Provide a brief narrative of the land use. (Max 750 characters)</span>',
            model: 'background.currentUse',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">General Conditions</span><br/><span class="help-text">Provide a brief overview of site observations, such as natural areas and condition, soil erosion observed in fields, etc. (Max 750 characters)</span>',
            model: 'background.generalConditions',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Watershed Context</span><br/><span class="help-text">Provide a description of how the site fits in with watershed priorities.  I.e - why is the location important? (Max 750 characters)</span>',
            model: 'background.watershedContext',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
    getGoals() {
      return {
        legend: 'Goals and Concerns',
        headerName: 'Goals and Concerns',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Landowner Goals</span><br/><span class="help-text">(Max 1000 characters)</span>',
            model: 'goals.landownerGoals',
            max: 1000,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Forest Management Goals</span><br/><span class="help-text">(Max 1000 characters)</span>',
            model: 'goals.forestManagementGoals',
            max: 1000,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Agricultural Management Goals</span><br/><span class="help-text">(Max 1000 characters)</span>',
            model: 'goals.agriculturalManagementGoals',
            max: 1000,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Resource Priorities</span><br/><span class="help-text">Identify the top resource priorities on the site, add comments as needed about location and severity (Max 1000 characters)</span>',
            model: 'goals.resourcePriorities',
            max: 1000,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Forest Resource Concerns</span><br/><span class="help-text">(Max 750 characters)</span>',
            model: 'goals.forestResourceConcerns',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Management Concerns</span><br/><span class="help-text">List potential concerns with site management, such as difficult access, limited management window due to flood inundation, etc. (Max 750 characters)</span>',
            model: 'goals.managementConcerns',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Other Concerns</span><br/><span class="help-text">Describe any other concerns. (Max 750 characters)</span>',
            model: 'goals.otherConcerns',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
    getPropertyOverview() {
      return {
        legend: 'Property Overview',
        headerName: 'Property Overview',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Topography</span><br/><span class="help-text">Describe topography. Example: steep slope south of Tualatin River, near confluence with Dairy Creek. (Max 500 characters)</span>',
            model: 'propertyOverview.topography',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Soil and Geology</span><br/><span class="help-text">Describe soils and geology. (Max 500 characters)</span>',
            model: 'propertyOverview.soilsAndGeology',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Water Resources</span><br/><span class="help-text">Describe hydrologic conditions observed, such as wetlands, inundation patterns and duration, etc. (Max 1000 characters)</span>',
            model: 'propertyOverview.waterResources',
            max: 1000,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Water Rights</span><br/><span class="help-text">Describe water rights. (Max 500 characters)</span>',
            model: 'propertyOverview.waterRights',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Vegetation Description</span><br/><span class="help-text">Describe vegetation observed and condition ‐ native, invasive, mature, logged recently, etc. (Max 500 characters)</span>',
            model: 'propertyOverview.vegetationDescription',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Wildlife Observations</span><br/><span class="help-text">Describe any wildlife observations or landowner information about wildlife. (Max 500 characters)</span>',
            model: 'propertyOverview.wildlifeObservations',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Habitat Value</span><br/><span class="help-text">Describe habitat context for the site. Is the site in a priority area for habitat improvement? (Max 500 characters)</span>',
            model: 'propertyOverview.habitatValue',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Cultural Resources</span><br/><span class="help-text">List any cultural resources observed or mentioned by the landowner either on site or nearby. (Max 500 characters)</span>',
            model: 'propertyOverview.culturalResources',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Invasive Species</span><br/><span class="help-text">Describe invasive species present on site. (Max 500 characters)</span>',
            model: 'propertyOverview.invasiveSpecies',
            max: 500,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Regulatory Compliance</span><br/><span class="help-text">Describe any potential issues or interactions around regulatory compliance concerns on site. (Max 750 characters)</span>',
            model: 'propertyOverview.regulatoryCompliance',
            max: 750,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
    getResourceAnalysis() {
      return {
        legend: 'Resource Analysis',
        headerName: 'Resource Analysis',
        fields: [
          {
            type: 'customRichText',
            label: '<span class="label-name">Recommendations</span><br/><span class="help-text">Provide a summary of the overall site assessment, general recommendations and general next steps. (Max 1000 characters)</span>',
            model: 'resourceAnalysis.recommendations',
            max: 1000,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
          {
            type: 'customRichText',
            label: '<span class="label-name">Additional Resource Inventories Recommended</span><br/><span class="help-text">List any additional resource inventories that need to be completed, such as soil tests or SVAPs. (Max 250 characters)</span>',
            model: 'resourceAnalysis.additionalResource',
            max: 250,
            attributes: {
              wrapper: { 'data-list': 'col-5' },
            },
            styleClasses: 'row assessment-text',
          },
        ],
      };
    },
  },
};
